import { axiosGet, axiosPost } from "./useAxios";

/**
 * CreweasyMobile controller — crew activity (mobile / web) endpoints.
 * Base URL comes from axiosInstance; every response is the ApiCommonResponse
 * envelope: { success, message, payload }.
 */

const CTRL = "/CreweasyMobile";

const payloadOf = (res) =>
  res && res.payload !== undefined ? res.payload : null;

export const session = () => ({
  airlineCode: sessionStorage.getItem("session_airlineCode") ?? "",
  employeeNumber: sessionStorage.getItem("session_employeeNumber") ?? "",
});

/** "yyyy-MM-dd HH:mm:ss" for the deviceTime parameter. */
export const deviceTime = (d = new Date()) => {
  const p = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ` +
    `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
  );
};

// Fixed for every crew-activity/get call — only employeeNumber (and airlineCode, from session) vary.
const DEVICE = {
  imei: "",
  deviceType: "web",
  appVersion: "",
  osVersion: "",
};

/* ------------------------------------------------------------------ dashboard */

/** POST /CreweasyMobile/get — day groups + segments for the dashboard. */
export async function getCrewScheduleActivity({
  airlineCode,
  employeeNumber,
} = {}) {
  const s = session();
  const res = await axiosPost(`${CTRL}/get`, {
    airlineCode: airlineCode ?? s.airlineCode,
    employeeNumber: employeeNumber ?? s.employeeNumber,
    imei: DEVICE.imei,
    deviceType: DEVICE.deviceType,
    appVersion: DEVICE.appVersion,
    osVersion: DEVICE.osVersion,
  });
  return payloadOf(res) ?? [];
}

/* --------------------------------------------------------------- crew members */

/** GET /CreweasyMobile/leg-crews — Flight detail view. */
export async function getLegCrews({
  airlineCode,
  employeeNumber,
  flightScheduleTime,
}) {
  const s = session();
  const res = await axiosGet(`${CTRL}/leg-crews`, {
    airlineCode: airlineCode ?? s.airlineCode,
    employeeNumber: employeeNumber ?? s.employeeNumber,
    flightScheduleTime,
  });
  return payloadOf(res) ?? [];
}

/** GET /CreweasyMobile/hotel-crews — Hotel Stay detail view. */
export async function getHotelCrews({
  airlineCode,
  employeeNumber,
  checkinTime,
}) {
  const s = session();
  const res = await axiosGet(`${CTRL}/hotel-crews`, {
    airlineCode: airlineCode ?? s.airlineCode,
    employeeNumber: employeeNumber ?? s.employeeNumber,
    checkinTime,
  });
  return payloadOf(res) ?? [];
}

/** GET /CreweasyMobile/hotel-transport-crews — Transport ( Hotel ) detail view. */
export async function getHotelTransportCrews({
  airlineCode,
  employeeNumber,
  pickupTime,
}) {
  const s = session();
  const res = await axiosGet(`${CTRL}/hotel-transport-crews`, {
    airlineCode: airlineCode ?? s.airlineCode,
    employeeNumber: employeeNumber ?? s.employeeNumber,
    pickupTime,
  });
  return payloadOf(res) ?? [];
}

/* ---------------------------------------------------------------------- hotel */

/** POST /CreweasyMobile/get-allocate-hotel-list — hotel select box. */
export async function getAllocateHotelList({
  airlineCode,
  employeeNumber,
  transactionId,
  locationCode = "",
}) {
  const s = session();
  const res = await axiosPost(`${CTRL}/get-allocate-hotel-list`, {
    airlineCode: airlineCode ?? s.airlineCode,
    employeeNumber: employeeNumber ?? s.employeeNumber,
    transactionId,
    locationCode: locationCode ?? "",
  });
  return payloadOf(res) ?? [];
}

/** POST /CreweasyMobile/change-hotel */
export async function changeHotel({
  airlineCode,
  employeeNumber,
  transactionId,
  hotelId,
}) {
  const s = session();
  return axiosPost(`${CTRL}/change-hotel`, {
    airlineCode: airlineCode ?? s.airlineCode,
    employeeNumber: employeeNumber ?? s.employeeNumber,
    transactionId,
    hotelId: Number(hotelId) || 0,
    deviceTime: deviceTime(),
  });
}

/** POST /CreweasyMobile/cancel-hotel */
export async function cancelHotel({
  airlineCode,
  employeeNumber,
  airportCode,
  transactionId,
  transporterRequired = false,
  addressId = 0,
}) {
  const s = session();
  return axiosPost(`${CTRL}/cancel-hotel`, {
    airlineCode: airlineCode ?? s.airlineCode,
    employeeNumber: employeeNumber ?? s.employeeNumber,
    airportCode: airportCode ?? "",
    transactionId,
    transporterRequired: transporterRequired === true,
    addressId: Number(addressId) || 0,
    deviceTime: deviceTime(),
  });
}

/* ------------------------------------------------------------ transportation */

/** GET /CreweasyMobile/get-cancel-transportation-reason */
export async function getCancelTransportationReasons({ airlineCode } = {}) {
  const s = session();
  const res = await axiosGet(`${CTRL}/get-cancel-transportation-reason`, {
    airlineCode: airlineCode ?? s.airlineCode,
  });
  return payloadOf(res) ?? [];
}

/** POST /CreweasyMobile/cancel-transportation */
export async function cancelTransportation(request) {
  const s = session();
  return axiosPost(`${CTRL}/cancel-transportation`, {
    airlineCode: s.airlineCode,
    employeeNumber: s.employeeNumber,
    deviceTime: deviceTime(),
    ...request,
  });
}

/** POST /CreweasyMobile/change-residence-location */
export async function changeResidenceLocation(request) {
  const s = session();
  return axiosPost(`${CTRL}/change-residence-location`, {
    airlineCode: s.airlineCode,
    employeeNumber: s.employeeNumber,
    deviceTime: deviceTime(),
    ...request,
  });
}

/* ------------------------------------------------------------------ trip / map */

/** GET /CreweasyMobile/get-trip-driver — driver, vehicle, crews. */
export async function getTripDriver({
  airlineCode,
  employeeNumber,
  transactionId,
}) {
  const s = session();
  const res = await axiosGet(`${CTRL}/get-trip-driver`, {
    airlineCode: airlineCode ?? s.airlineCode,
    transactionId,
    employeeNumber: employeeNumber ?? s.employeeNumber,
  });
  return payloadOf(res);
}

/** GET /CreweasyMobile/get-trip-security-guard */
export async function getTripSecurityGuard({
  airlineCode,
  employeeNumber,
  transactionId,
}) {
  const s = session();
  const res = await axiosGet(`${CTRL}/get-trip-security-guard`, {
    airlineCode: airlineCode ?? s.airlineCode,
    transactionId,
    employeeNumber: employeeNumber ?? s.employeeNumber,
  });
  return payloadOf(res);
}

/** GET /CreweasyMobile/get-trip-tracking — vehicle position + passengers. */
export async function getTripTracking({
  airlineCode,
  employeeNumber,
  transactionId,
}) {
  const s = session();
  const res = await axiosGet(`${CTRL}/get-trip-tracking`, {
    airlineCode: airlineCode ?? s.airlineCode,
    transactionId,
    employeeNumber: employeeNumber ?? s.employeeNumber,
  });
  return payloadOf(res);
}

/** GET /CreweasyMobile/get-trip-driver-eta */
export async function getTripDriverEta({
  airlineCode,
  employeeNumber,
  transactionId,
}) {
  const s = session();
  const res = await axiosGet(`${CTRL}/get-trip-driver-eta`, {
    airlineCode: airlineCode ?? s.airlineCode,
    transactionId,
    employeeNumber: employeeNumber ?? s.employeeNumber,
  });
  return payloadOf(res);
}
/** GET /CreweasyMobile/get-vehice-gps-location */
export async function getVehicleGpsLocation({
  airlineCode,
  employeeNumber,
  transactionId,
}) {
  const s = session();
  const res = await axiosGet(`${CTRL}/get-vehicle-gps-location`, {
    airlineCode: airlineCode ?? s.airlineCode,
    transactionId,
    employeeNumber: employeeNumber ?? s.employeeNumber,
  });
  return payloadOf(res);
}
/* -------------------------------------------------------------------- address */

/** GET /CreweasyMobile/station-address — address select box. */
export async function getStationAddress({
  airlineCode,
  employeeNumber,
  airportCode,
}) {
  const s = session();
  const res = await axiosGet(`${CTRL}/station-address`, {
    airlineCode: airlineCode ?? s.airlineCode,
    employeeNumber: employeeNumber ?? s.employeeNumber,
    airportCode: airportCode ?? "",
  });
  return payloadOf(res) ?? [];
}

/* --------------------------------------------------------------------- images */

/** GET /CreweasyMobile/local-vendor-vehicle-image */
export async function getLocalVendorVehicleImages(transporterId) {
  const res = await axiosGet(`${CTRL}/local-vendor-vehicle-image`, {
    transporterId: Number(transporterId) || 0,
  });
  return payloadOf(res) ?? [];
}

/** GET /CreweasyMobile/hotel-image */
export async function getHotelImages(hotelId) {
  const res = await axiosGet(`${CTRL}/hotel-image`, {
    hotelId: Number(hotelId) || 0,
  });
  return payloadOf(res) ?? [];
}

/** GET /CreweasyMobile/hotel-shuttle-image */
export async function getHotelShuttleImages(hotelId) {
  const res = await axiosGet(`${CTRL}/hotel-shuttle-image`, {
    hotelId: Number(hotelId) || 0,
  });
  return payloadOf(res) ?? [];
}

/** GET /CreweasyMobile/activity-location-image */
export async function getActivityLocationImages(activityLocationCode) {
  const res = await axiosGet(`${CTRL}/activity-location-image`, {
    activityLocationCode,
  });
  return payloadOf(res) ?? [];
}

/** GET /CreweasyMobile/airport-simulator-image */
export async function getAirportSimulatorImages(airportCode) {
  const res = await axiosGet(`${CTRL}/airport-simulator-image`, {
    airportCode,
  });
  return payloadOf(res) ?? [];
}

/**
 * Images for whichever segment kind is expanded — picks the right endpoint from
 * the segment's own detail object.
 */
export async function getSegmentImages(activity) {
  if (!activity) return [];
  if (activity.showLocalTransport === true) {
    return getLocalVendorVehicleImages(
      activity.localTransportDetails?.transporterId,
    );
  }
  if (activity.showHotelTransport === true) {
    return getHotelShuttleImages(activity.hotelTransportDetails?.hotelId);
  }
  if (activity.showHotel === true) {
    return getHotelImages(activity.hotelDetails?.hotelId);
  }
  if (activity.showGroundDuty === true) {
    return getActivityLocationImages(
      activity.groundDutyDetails?.activityLocationCode,
    );
  }
  if (activity.showAirportDuty === true) {
    return getAirportSimulatorImages(
      activity.airportDutyDetails?.airportCode ?? activity.airportCode,
    );
  }
  return [];
}
